"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import react, { useRef, useState } from "react";
import { cn } from "../../utils/cn.js";
import { Button } from "../Button/Button.js";
import { Span } from "../Text/Span.js";
import { SlidingNumber } from "../animations/SlidingNumber/index.js";
import { counterIconButtonVariants, counterVariants } from "./Counter.variants.js";
import { CounterIconButton } from "./CounterIconButton.js";
function Counter({ iconButtonType = 'primary', incrementIconButtonProps = {}, decrementIconButtonProps = {}, ctaButtonProps = {}, emphasis = 'regular', min = 0, max = 10, cta = false, label, className = '', size, disabled = false, handleValueChange, value = min, decrementIconTooltipProps, incrementIconTooltipProps, useSlidingNumber = false }) {
    const id = react.useId();
    const incrementButtonRef = useRef(null);
    const decrementButtonRef = useRef(null);
    const ctaButtonRef = useRef(null);
    const showCta = cta && 0 === value;
    const [displayCta, setDisplayCta] = useState(showCta);
    const hideCta = ()=>{
        handleValueChange(++value);
        setDisplayCta(false);
        setTimeout(()=>{
            incrementButtonRef.current?.focus();
        }, 0);
    };
    const decrementValue = ()=>{
        const count = --value;
        if (count === min && cta) {
            setDisplayCta(true);
            setTimeout(()=>{
                ctaButtonRef.current?.focus();
            }, 0);
        }
        handleValueChange(count);
        if (value <= min) setTimeout(()=>{
            incrementButtonRef.current?.focus();
        }, 0);
    };
    const incrementValue = ()=>{
        handleValueChange(++value);
        if (value >= max) setTimeout(()=>{
            decrementButtonRef.current?.focus();
        }, 0);
    };
    const minusDisabled = disabled || value <= min;
    const plusDisabled = disabled || value >= max;
    const minusLabel = `Decrease ${label}${minusDisabled ? ' - disabled' : ''}`;
    const plusLabel = `Increase ${label}${plusDisabled ? ' - disabled' : ''}`;
    const addButtonType = counterIconButtonVariants[iconButtonType].add;
    const removeButtonType = counterIconButtonVariants[iconButtonType].remove;
    const { size: ctaSize = 'sm', children: ctaChildren = 'Add', iconLeft = 'add', ...restProps } = ctaButtonProps;
    const { root, ctaButton, numericText } = counterVariants({
        emphasis,
        size
    });
    const rootClasses = cn(root(), className);
    if (displayCta) return /*#__PURE__*/ jsx(Button, {
        "data-testid": "counter-cta-button",
        size: ctaSize,
        onClick: hideCta,
        type: iconButtonType,
        iconLeft: iconLeft,
        emphasis: emphasis,
        disabled: disabled,
        ref: ctaButtonRef,
        className: ctaButton(),
        ...restProps,
        children: ctaChildren
    });
    return /*#__PURE__*/ jsxs("fieldset", {
        "aria-label": `${label} counter controls`,
        className: rootClasses,
        children: [
            /*#__PURE__*/ jsx("input", {
                "aria-label": `${label} counter value`,
                className: "sr-only",
                max: max,
                min: min,
                readOnly: true,
                tabIndex: -1,
                type: "number",
                value: value
            }),
            /*#__PURE__*/ jsx(CounterIconButton, {
                ref: decrementButtonRef,
                type: "decrement",
                ariaLabel: minusLabel,
                ariaControls: id,
                onClick: decrementValue,
                tooltipProps: decrementIconTooltipProps,
                buttonType: removeButtonType,
                disabled: minusDisabled,
                emphasis: emphasis,
                iconButtonProps: decrementIconButtonProps
            }),
            useSlidingNumber ? /*#__PURE__*/ jsx("div", {
                id: id,
                className: "w-10 items-center justify-center text-center whitespace-nowrap",
                "aria-live": "polite",
                "aria-atomic": "true",
                children: /*#__PURE__*/ jsx(SlidingNumber, {
                    value: value,
                    className: numericText(),
                    "data-testid": "counter-value"
                })
            }) : /*#__PURE__*/ jsx(Span, {
                align: "center",
                id: id,
                "aria-live": "polite",
                "aria-atomic": "true",
                className: numericText(),
                "data-testid": "counter-value",
                children: value
            }),
            /*#__PURE__*/ jsx(CounterIconButton, {
                ref: incrementButtonRef,
                type: "increment",
                ariaLabel: plusLabel,
                ariaControls: id,
                onClick: incrementValue,
                tooltipProps: incrementIconTooltipProps,
                buttonType: addButtonType,
                disabled: plusDisabled,
                emphasis: emphasis,
                iconButtonProps: incrementIconButtonProps
            })
        ]
    });
}
export { Counter };
